// StudyList: the side list of case studies / posts. From the md breakpoint up it is the full vertical list; below it,
// StudyPicker: the selected entry as a pill with a chevron that opens an M3 menu. Use StudyList so every page behaves the same.
// The menu shows at most 5 entries plus half of the next, so it is clear that it scrolls without becoming a huge list.
// Props (both): items=[{ key, title, supporting?, selected? }], onSelect(key), disabled, label (aria-label)
(function () {
  const { Box, Typography, ButtonBase } = MaterialUI;
  const { useState, useRef } = React;

  const ROW = 56;      // MenuItem height
  const VISIBLE = 5.5; // entries visible at once when the list is longer than 5

  function StudyPicker({ items = [], onSelect, disabled = false, label = 'Choose' }) {
    const { Menu, MenuItem, GoogleIcon } = window.Components;
    const [open, setOpen] = useState(false);
    const [width, setWidth] = useState(280);
    const ref = useRef(null);
    const current = items.find(i => i.selected) || items[0];
    if (!current) return null;
    const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';
    const toggle = () => { if (ref.current) setWidth(ref.current.offsetWidth); setOpen(o => !o); };

    return (
      <>
        <ButtonBase
          ref={ref}
          onClick={toggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          sx={{
            position: 'relative', display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
            pl: 2, pr: 1.5, py: 1.25, minHeight: 56, borderRadius: 'var(--m3-shape-full)', fontFamily: 'inherit',
            color: 'var(--m3-on-secondary-container)', backgroundColor: 'var(--m3-secondary-container)',
            '&::before': { content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', backgroundColor: 'currentColor', opacity: 0, transition: `opacity ${motion}` },
            '&:hover::before': { opacity: 'var(--m3-state-hover)' },
            '&:focus-visible::before': { opacity: 'var(--m3-state-focus)' },
            '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: -3 }
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <span className="m3-sr-only">{label}: </span>
            <Typography variant="body2" component="span" noWrap sx={{ display: 'block', fontWeight: 700, lineHeight: 1.3 }}>{current.title}</Typography>
            {current.supporting && <Typography variant="caption" component="span" noWrap sx={{ display: 'block', opacity: 0.8 }}>{current.supporting}</Typography>}
          </Box>
          <GoogleIcon name="expand_more" size={24} style={{ color: 'inherit', transform: open ? 'rotate(180deg)' : 'none', transition: `transform ${motion}` }} />
        </ButtonBase>
        <Menu
          anchorEl={ref.current}
          open={open}
          onClose={() => setOpen(false)}
          minWidth={width}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, borderRadius: 'var(--m3-shape-extra-large)', maxWidth: width } }}
          MenuListProps={{ role: 'listbox', sx: { p: 0, ...(items.length > 5 && { maxHeight: ROW * VISIBLE, overflowY: 'auto' }) } }}
        >
          {items.map(i => (
            <MenuItem key={i.key} selected={i.key === current.key} headline={i.title} supportingText={i.supporting}
              sx={{ height: ROW, my: 0 }} onClick={() => { setOpen(false); onSelect(i.key); }} />
          ))}
        </Menu>
      </>
    );
  }

  function StudyList({ items = [], onSelect, disabled = false, label = 'Choose' }) {
    const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';
    return (
      <>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <StudyPicker items={items} onSelect={onSelect} disabled={disabled} label={label} />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'contents' } }}>
          {items.map(i => (
            <ButtonBase
              key={i.key}
              onClick={() => onSelect(i.key)}
              disabled={disabled}
              aria-current={i.selected ? 'page' : undefined}
              sx={{
                position: 'relative', display: 'block', textAlign: 'left', width: '100%', px: 2, py: 1.25,
                borderRadius: 'var(--m3-shape-full)', fontFamily: 'inherit',
                color: i.selected ? 'var(--m3-on-secondary-container)' : 'var(--m3-on-surface-variant)',
                backgroundColor: i.selected ? 'var(--m3-secondary-container)' : 'transparent',
                transition: `background-color ${motion}, color ${motion}`,
                '&::before': { content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', backgroundColor: 'currentColor', opacity: 0, transition: `opacity ${motion}` },
                '&:hover::before': { opacity: 'var(--m3-state-hover)' },
                '&:focus-visible::before': { opacity: 'var(--m3-state-focus)' },
                '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2 }
              }}
            >
              <Typography variant="body2" component="span" sx={{ display: 'block', fontWeight: i.selected ? 700 : 500, lineHeight: 1.3 }}>{i.title}</Typography>
              {i.supporting && <Typography variant="caption" component="span" sx={{ display: 'block', opacity: 0.8 }}>{i.supporting}</Typography>}
            </ButtonBase>
          ))}
        </Box>
      </>
    );
  }

  window.Components = window.Components || {};
  window.Components.StudyList = StudyList;
  window.Components.StudyPicker = StudyPicker;
})();
